import React, {useEffect} from 'react';
import {Platform, useWindowDimensions, View} from 'react-native';
import {useSelector} from 'react-redux';
import {useIsFocused} from '@react-navigation/native';
import {get} from 'lodash';

import Carousel from '/component/base/Carousel';
import LoadingIndicator from '/component/base/LoadingIndicator';
import Text from '/component/base/Text';
import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import Gutter from '/component/layout/Gutter';
import BaseScreen from '/component/partial/BaseScreen';
import Timestamp from '/component/partial/Timestamp';
import {MAX_CONTENT_WIDTH} from '/constant/data.constant';
import {indent} from '/constant/layout.constant';
import {Shared, TeamsID} from '/constant/TestIDs';
import {SCREEN_NAMES} from '/enum/tealium.enum';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {clearScreenParams} from '/util/async-storage';
import {shouldReleaseFeature} from '/util/featureRelease.util';

import BonusesSection from './BonusesSection';
import CloseToCompetencySection from './CloseToCompetencySection';
import CoachesSection from './CoachesSection';
import CoachesTeamSummary from './CoachesTeamsSummaryCard';
import CoachesToCelebrateSection from './CoachesToCelebrateSection';
import NeedAttentionSection from './NeedAttentionSection';
import RankPerformanceSection from './RankPerformanceSection';
import {useTeams} from './Teams.hook';
import {styles} from './Teams.style';
import {TeamsProps} from './Teams.types';
import TeamsOverviewSection from './TeamsOverviewSection';
import TeamsVolumeSummaryCard from './TeamsVolumeSummaryCard';

const WEB_CARD_GAP = 5;
const WEB_CAROUSEL_HORIZONTAL_PADDING = indent + indent * 0.75;

export const Teams = ({
  showChatFeature = true,
  hasTeamsMetricsById,
  onNavigationIconPress,
  onShowReport = () => {},
  onShowInfoPopover = () => {},
  onShowRefreshInfoPopover = () => {},
  getTeamsMetrics,
  getSameDayLastMonth,
  hasSameDayLastMonth,
}: TeamsProps) => {
  const {fontScale} = useWindowDimensions();
  const isFontResizeEnabled = fontScale > 1;

  const {tt} = useTeams({
    hasTeamsMetricsById,
    getTeamsMetrics,
    getSameDayLastMonth,
    hasSameDayLastMonth,
  });

  const isFocused = useIsFocused();
  const {width} = useWindowDimensions();

  useEffect(() => {
    if (isFocused && Platform.OS === 'web') {
      clearScreenParams(SCREEN_NAMES.myTeamsReport);
    }
  }, [isFocused]);
  const featureFlag = useSelector(variablesFeatureFlag);
  const HideCoachesToCelebrate = shouldReleaseFeature(
    get(featureFlag, 'ff_hide_coaches_to_celebrate', ''),
  );

  const IsWeb = Platform.OS == 'web';
  const isSmallScreen = width < 750;
  const availableWidth =
    IsWeb && !isSmallScreen ? Math.min(width, MAX_CONTENT_WIDTH) : width - 80;
  const webMultiCardAvailableWidth = Math.max(
    0,
    availableWidth - WEB_CAROUSEL_HORIZONTAL_PADDING,
  );
  const webSummaryCardWidth = (webMultiCardAvailableWidth - WEB_CARD_GAP) / 2;
  const ScreenOSBasedWidth = IsWeb ? MAX_CONTENT_WIDTH / 2.2 : width - 30;
  const baseCardHeight = IsWeb ? 240 : 250;
  const dynamicCardHeight = Math.min(
    baseCardHeight + (fontScale - 1) * 160,
    baseCardHeight + 220,
  );

  return (
    <AccessibilityWrapper>
      <BaseScreen
        testID={TeamsID.teamsScreen}
        showChatFeature={showChatFeature}
        onNavigationIconPress={onNavigationIconPress}>
        <Gutter style={styles.header}>
          <View
            accessible={true}
            accessibilityLabel="Teams"
            accessibilityRole="header">
            <Text
              accessible={true}
              accessibilityRole="header"
              testID={Shared.sharedScreenTitle}
              variant="xLarge"
              style={styles.headerText}>
              {tt('header.text', 'Teams')}
            </Text>
          </View>
        </Gutter>
        <View style={styles.timestampContainer}>
          <Timestamp
            onShowInfoPopover={onShowRefreshInfoPopover}
            infoButtonLabel="Show teams data refresh info"
          />
        </View>
        {hasTeamsMetricsById ? (
          <>
            {IsWeb && !isSmallScreen && !isFontResizeEnabled ? (
              <View style={styles.webSectionSpacing}>
                <Gutter style={styles.summaryCardsContainerWeb}>
                  <View style={styles.summaryCardsRow}>
                    <AccessibilityWrapper>
                      <CoachesTeamSummary
                        style={{
                          ...styles.summaryCardWeb,
                          width: webSummaryCardWidth,
                          marginRight: WEB_CARD_GAP,
                          minHeight: dynamicCardHeight,
                        }}
                      />
                    </AccessibilityWrapper>
                    <AccessibilityWrapper>
                      <TeamsVolumeSummaryCard
                        style={{
                          ...styles.summaryCardWeb,
                          width: webSummaryCardWidth,
                          minHeight: dynamicCardHeight,
                        }}
                      />
                    </AccessibilityWrapper>
                  </View>
                </Gutter>
              </View>
            ) : (
              <Carousel
                cardTitles={['Coaches Team Summary', 'Team Volumes Summary']}
                webFitContainer={IsWeb}
                snapToInterval={ScreenOSBasedWidth + WEB_CARD_GAP}
                indicatorDots={true}>
                <AccessibilityWrapper>
                  <CoachesTeamSummary
                    style={{
                      width: IsWeb ? '100%' : ScreenOSBasedWidth,
                      minHeight: dynamicCardHeight,
                    }}
                  />
                </AccessibilityWrapper>
                <AccessibilityWrapper>
                  <TeamsVolumeSummaryCard
                    style={{
                      width: IsWeb ? '100%' : ScreenOSBasedWidth,
                      minHeight: dynamicCardHeight,
                    }}
                  />
                </AccessibilityWrapper>
              </Carousel>
            )}
            <View style={styles.content}>
              {!HideCoachesToCelebrate && (
                <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                  <CoachesToCelebrateSection
                    onShowReport={onShowReport}
                    onShowInfoPopover={onShowInfoPopover}
                  />
                </View>
              )}
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <CoachesSection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <TeamsOverviewSection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <RankPerformanceSection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <CloseToCompetencySection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <BonusesSection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
              <View style={IsWeb ? styles.webSectionSpacing : undefined}>
                <NeedAttentionSection
                  onShowReport={onShowReport}
                  onShowInfoPopover={onShowInfoPopover}
                />
              </View>
            </View>
          </>
        ) : (
          <LoadingIndicator style={styles.loadingIndicator} />
        )}
      </BaseScreen>
    </AccessibilityWrapper>
  );
};

export default Teams;
